import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { FinanceService, FinanceStats } from '../../../../core/services/finance.service';

@Component({
  selector: 'app-web-orders-finance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ChartModule,
    ButtonModule,
    DatePickerModule,
    TagModule,
    TooltipModule,
    TableModule
  ],
  templateUrl: './web-orders-finance.html',
  styleUrl: './web-orders-finance.css'
})
export class WebOrdersFinance implements OnInit {
  stats: FinanceStats | null = null;
  loading = true;
  rangeDates: Date[] = [];
  seriesChart: any;
  embudoChart: any;
  mixMetodoChart: any;
  mixCanalChart: any;
  chartOptions: any;
  pieOptions: any;
  countPieOptions: any;

  constructor(
    private financeService: FinanceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 29);
    this.rangeDates = [from, to];
    this.initChartOptions();
    this.loadStats();
  }

  private toIso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  loadStats() {
    this.loading = true;
    const from = this.rangeDates?.[0] ? this.toIso(this.rangeDates[0]) : undefined;
    const to = this.rangeDates?.[1] ? this.toIso(this.rangeDates[1]) : from;
    this.financeService.getStats(from, to).subscribe({
      next: (data) => {
        this.stats = data;
        this.buildCharts(data);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onRangeChange() {
    if (this.rangeDates?.[0] && this.rangeDates?.[1]) {
      this.loadStats();
    }
  }

  private buildCharts(data: FinanceStats) {
    const serie = data.serieTemporal;
    this.seriesChart = {
      labels: serie?.labels || [],
      datasets: [
        {
          type: 'bar',
          label: 'Cobrado (S/)',
          data: serie?.cobrado || [],
          backgroundColor: '#3b82f6',
          borderRadius: 6,
          yAxisID: 'y'
        },
        {
          type: 'line',
          label: 'Pedidos creados',
          data: serie?.pedidosCreados || [],
          borderColor: '#10b981',
          backgroundColor: 'rgba(16,185,129,0.15)',
          tension: 0.3,
          fill: false,
          yAxisID: 'y1'
        }
      ]
    };

    this.embudoChart = {
      labels: ['Pendientes', 'Aprobados', 'Rechazados'],
      datasets: [
        {
          data: [
            data.embudoPendientes || 0,
            data.embudoAprobados || 0,
            data.embudoRechazados || 0
          ],
          backgroundColor: ['#f59e0b', '#22c55e', '#ef4444'],
          hoverBackgroundColor: ['#d97706', '#16a34a', '#dc2626']
        }
      ]
    };

    this.mixMetodoChart = {
      labels: (data.mixMetodo || []).map((m) => m.name),
      datasets: [
        {
          data: (data.mixMetodo || []).map((m) => m.amount),
          backgroundColor: ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#64748b'],
        }
      ]
    };

    this.mixCanalChart = {
      labels: (data.mixCanal || []).map((m) => m.name),
      datasets: [
        {
          data: (data.mixCanal || []).map((m) => m.amount),
          backgroundColor: ['#0ea5e9', '#a855f7', '#94a3b8'],
        }
      ]
    };
  }

  private initChartOptions() {
    const textColorSecondary = '#6b7280';
    const surfaceBorder = '#e5e7eb';
    this.chartOptions = {
      maintainAspectRatio: false,
      aspectRatio: 0.7,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.dataset.label || '';
              const val = ctx.parsed.y;
              if (ctx.dataset.yAxisID === 'y1') return `${label}: ${val}`;
              return `${label}: ${new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(val || 0)}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: { color: textColorSecondary },
          grid: { color: 'transparent' }
        },
        y: {
          position: 'left',
          ticks: {
            color: textColorSecondary,
            callback: (v: any) => 'S/ ' + v
          },
          grid: { color: surfaceBorder, borderDash: [5, 5] }
        },
        y1: {
          position: 'right',
          ticks: { color: textColorSecondary },
          grid: { drawOnChartArea: false }
        }
      }
    };

    this.pieOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.label || '';
              const val = ctx.parsed || 0;
              return `${label}: ${new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(val)}`;
            }
          }
        }
      }
    };

    this.countPieOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.label || ''}: ${ctx.parsed || 0}`
          }
        }
      }
    };
  }
}
